import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import moment from 'moment';
import ReactDateRangePicker from '../../../components/ReactDateRangePicker/ReactDateRangePicker';
import AddSingleMemberDropdown from '../../../components/CustomDropdown/AddSingleMemberDropdown';
import FilterDropDownStyle from '../../../components/DropdownStyle/FilterDropDownStyle';
import Select from '../../../components/ReactSelectDropdown';
import { filterOption } from '../../../components/ReactSelectDropdown/ReactSelectDropdown';
import SearchWithButton from '../../../components/Search/SearchWithButton';
import CreateProjectDropdown from '../../../components/DropdownStyle/CreateProjectDropdown';
import searchIcon from '../../../assets/img/icons/search-white.svg';
import {
    FilterLabel,
    InputWithLabelSection,
} from '../../../styledComponents/common';
import { TaskStatusOptions } from '../../../modules/constants';

const CateringFilterContainer = styled.div`
  display: grid;
  grid-template-columns: 180px 40px 240px 280px 180px;
  grid-column-gap: 30px;
  align-items: flex-end;
  width: 100%;
  margin-bottom: 20px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
`;

const DatePickerWrapper = styled.div`
  .DateRangePickerInput {
    height: 40px;
    display: flex;
    align-items: center;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e0e4ec;
    background-color: #ffffff;
  }
  .DateInput {
    width: 100px;
    background-color: transparent;
  }
  .DateInput_input {
    font-size: 13px;
    padding: 8px 12px;
    color: #3d4d69;
    font-weight: 400;
  }
  .DateRangePickerInput_arrow {
      padding: 0 5px;
  }
`;

const CustomSearchContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: #ffffff;
  border: 1px solid #e0e4ec;
  border-radius: 8px;
  height: 40px;
  width: 100%;
  padding-left: 15px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: #32a8aa;
    box-shadow: 0 0 0 2px rgba(50, 168, 170, 0.1);
  }
`;

const CustomSearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 13px;
  color: #3d4d69;
  height: 100%;

  &::placeholder {
    color: #a4b0c1;
  }
`;

const CustomSearchButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #4b5a71;
  width: 50px;
  height: 100%;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #3d4d69;
  }

  img {
    width: 18px;
    height: 18px;
  }
`;

const CateringTaskFilter = (props) => {
    const { t } = useTranslation();
    const {
        getOrganizationMembersList,
        organizationMembersList,
        getTaskLocation,
        locationsList,
        selectedLocation,
        selectOffice,
        selectedMember,
        selectMember,
        clearMember,
        taskStatus,
        handleStatusChange,
        selectedDate,
        handleDateChange,
        searchTerm,
        onSearchTermChange,
        handleSearchByName,
        handleClearSearch,
    } = props;

    const [selectedDates, setSelectedDates] = useState(selectedDate);
    const [locationOptions, setLocationOptions] = useState([]);

    const statusOptions = TaskStatusOptions?.map(option => ({
        ...option,
        label: t(option.key),
    }));

    useEffect(() => {
        getTaskLocation({ is_catering_location: true });
        getOrganizationMembersList({ office_id: null });
    }, []);

    useEffect(() => {
        let newArr = [
            {
                label: t('all_locations'),
                value: null,
            },
        ];
        if (locationsList && locationsList.length > 0) {
            locationsList.forEach((location) => {
                newArr.push({
                    label: location.name,
                    value: location.id,
                });
            });
        }
        setLocationOptions(newArr);
    }, [locationsList, t]);

    const onDateApply = () => {
        handleDateChange(selectedDates);
    };

    const selectLastMonth = () => {
        setSelectedDates({
            startDate: moment().subtract(29, 'days'),
            endDate: moment(),
        });
    };

    const selectLastWeek = () => {
        setSelectedDates({
            startDate: moment().subtract(7, 'days'),
            endDate: moment(),
        });
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearchByName();
        }
    };

    return (
        <CateringFilterContainer>
            <InputWithLabelSection>
                <FilterLabel>{t('location')}</FilterLabel>
                <Select
                    isSearchable
                    value={selectedLocation?.value === null ? { ...selectedLocation, label: t('all_locations') } : selectedLocation}
                    options={locationOptions}
                    placeholder={t('select_location')}
                    onChange={selectOffice}
                    styles={CreateProjectDropdown({ height: '40px' })}
                    filterOption={filterOption}
                />
            </InputWithLabelSection>

            <InputWithLabelSection>
                <FilterLabel>{t('assignee')}</FilterLabel>
                <div style={{ width: `40px`, height: '40px', display: 'flex', alignItems: 'center' }}>
                    <AddSingleMemberDropdown
                        assigneeManage={selectedMember}
                        updateState={selectMember}
                        membersList={organizationMembersList}
                        clearAssignee={clearMember}
                    />
                </div>
            </InputWithLabelSection>

            <InputWithLabelSection>
                <FilterLabel>{t('search')}</FilterLabel>
                <CustomSearchContainer>
                    <CustomSearchInput
                        value={searchTerm}
                        onChange={onSearchTermChange}
                        onKeyDown={handleKeyPress}
                        placeholder={`${t('search_by')} ${t('task_name')}...`}
                    />
                    <CustomSearchButton onClick={handleSearchByName}>
                        <img src={searchIcon} alt="search" />
                    </CustomSearchButton>
                </CustomSearchContainer>
            </InputWithLabelSection>

            <InputWithLabelSection>
                <FilterLabel>{t('date_range')}</FilterLabel>
                <DatePickerWrapper>
                    <ReactDateRangePicker
                        startDate={selectedDates.startDate ? selectedDates.startDate : null}
                        startDateId="catering-report-start-date"
                        endDate={selectedDates.endDate ? selectedDates.endDate : null}
                        endDateId="catering-report-end-date"
                        onDatesChange={setSelectedDates}
                        dateDisplayFormat="DD/MM/YYYY"
                        onDateSelect={onDateApply}
                        selectLastWeek={selectLastWeek}
                        selectLastMonth={selectLastMonth}
                    />
                </DatePickerWrapper>
            </InputWithLabelSection>

            <InputWithLabelSection>
                <FilterLabel>{t('status')}</FilterLabel>
                <Select
                    isSearchable={false}
                    value={taskStatus?.value === null ? { ...taskStatus, label: t('all_status') } : taskStatus}
                    options={statusOptions}
                    placeholder={t('status')}
                    onChange={handleStatusChange}
                    styles={FilterDropDownStyle({
                        height: '40px',
                        width: '100%',
                        menuWidth: '100%',
                    })}
                />
            </InputWithLabelSection>
        </CateringFilterContainer>
    );
};

export default CateringTaskFilter;
